import CloseRounded from "@mui/icons-material/CloseRounded";
import TuneRounded from "@mui/icons-material/TuneRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Switch,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { getGoogleMapsApiKey } from "@bw/shared/components";
import { FacilityScopePicker } from "../../../common/components/FacilityScopePicker";
import { MaterialAreaPicker } from "../../../common/components/MaterialAreaPicker";
import { NearbyControl } from "../../../common/components/NearbyControl";
import {
  distanceOptions,
  radiusLabel,
  type MeasurementSystem,
} from "../../../common/distanceUnits";
import type { NearbyCenter } from "../../../common/hooks/useNearbyCenter";

export interface TournamentDiscoveryFiltersProps {
  desktopExpanded?: boolean;
  facilityId?: string;
  city?: string;
  startsAfter?: string;
  tournamentType?: "individual" | "team";
  eventMode?: "singles" | "doubles" | "mixed_doubles";
  eventGrade?: "A" | "B" | "C" | "D" | "J";
  officialOnly?: boolean;
  locationLabel?: string;
  latitude?: number;
  longitude?: number;
  radiusKm: number;
  measurementSystem: MeasurementSystem;
  coordinateSearchAllowed: boolean;
  onFacilityChange: (facilityId: string | undefined) => void;
  onCityChange: (city: string | undefined) => void;
  onStartsAfterChange: (date: string | undefined) => void;
  onTournamentTypeChange: (value: "individual" | "team" | undefined) => void;
  onEventModeChange: (
    value: "singles" | "doubles" | "mixed_doubles" | undefined,
  ) => void;
  onEventGradeChange: (value: "A" | "B" | "C" | "D" | "J" | undefined) => void;
  onOfficialOnlyChange: (value: boolean) => void;
  onLocationChange: (center: NearbyCenter) => void;
  onRadiusChange: (radiusKm: number) => void;
  onClearLocation: () => void;
  onClearAll: () => void;
}

/**
 * Progressive tournament-discovery filters. The compact row is intentionally
 * shared by desktop and mobile; the full controls live in an accessible sheet
 * rather than wrapping into a dense wall of unrelated chips.
 */
export function TournamentDiscoveryFilters({
  desktopExpanded = false,
  facilityId,
  city,
  startsAfter,
  tournamentType,
  eventMode,
  eventGrade,
  officialOnly = false,
  locationLabel,
  latitude,
  longitude,
  radiusKm,
  measurementSystem,
  coordinateSearchAllowed,
  onFacilityChange,
  onCityChange,
  onStartsAfterChange,
  onTournamentTypeChange,
  onEventModeChange,
  onEventGradeChange,
  onOfficialOnlyChange,
  onLocationChange,
  onRadiusChange,
  onClearLocation,
  onClearAll,
}: TournamentDiscoveryFiltersProps) {
  const [open, setOpen] = useState(false);
  const desktopViewport = useMediaQuery("(min-width:900px)", { noSsr: true });
  const expanded = desktopExpanded && desktopViewport;
  const addressSearchAvailable =
    coordinateSearchAllowed && Boolean(getGoogleMapsApiKey());
  const [areaDraft, setAreaDraft] = useState(locationLabel ?? "");
  const [cityDraft, setCityDraft] = useState(city ?? "");
  useEffect(
    () => setAreaDraft(locationLabel ?? city ?? ""),
    [locationLabel, city],
  );
  useEffect(() => setCityDraft(city ?? ""), [city]);

  const center = useMemo<NearbyCenter | null>(() => {
    if (latitude === undefined || longitude === undefined) return null;
    return {
      latitude,
      longitude,
      label: locationLabel,
      coarse: Boolean(locationLabel),
    };
  }, [latitude, longitude, locationLabel]);
  const activeCount =
    Number(Boolean(facilityId)) +
    Number(Boolean(startsAfter)) +
    Number(Boolean(tournamentType)) +
    Number(Boolean(eventMode)) +
    Number(Boolean(eventGrade)) +
    Number(Boolean(center || city));
  const clearAll = () => {
    setAreaDraft("");
    setCityDraft("");
    onClearAll();
  };
  const applyCity = () => {
    if (center) return;
    const nextCity =
      (addressSearchAvailable ? areaDraft : cityDraft).trim() || undefined;
    if (nextCity !== city) {
      if (nextCity) onClearLocation();
      onCityChange(nextCity);
    }
  };
  const dateLabel = startsAfter
    ? new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      }).format(new Date(`${startsAfter}T00:00:00Z`))
    : null;

  const filterFields = (
    <Stack spacing={3} sx={{ pt: 1 }}>
      {/* Phones toggle Official only from the chip in the filter row instead. */}
      {expanded ? (
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={officialOnly}
                onChange={(event) => onOfficialOnlyChange(event.target.checked)}
              />
            }
            label={
              <Typography sx={{ fontWeight: 800 }}>Official only</Typography>
            }
          />
          <Typography variant="body2" color="text.secondary">
            Tournaments sanctioned by Badminton Warriors. Their results count
            toward your BW rating.
          </Typography>
        </Box>
      ) : null}
      <Box>
        <Typography
          component={expanded ? "h3" : "h2"}
          variant="subtitle1"
          sx={{ fontWeight: 800 }}
        >
          Location
        </Typography>
        {addressSearchAvailable ? (
          <MaterialAreaPicker
            label="City, ZIP code, or area"
            placeholder="Milpitas, CA 95035"
            value={areaDraft}
            onValueChange={setAreaDraft}
            onAreaSelect={(area) => {
              setCityDraft("");
              onCityChange(undefined);
              setAreaDraft(area.label);
              onLocationChange({
                latitude: area.lat,
                longitude: area.lng,
                label: area.label,
                coarse: true,
              });
            }}
          />
        ) : null}
        {coordinateSearchAllowed ? (
          <Box sx={{ mt: 1.5 }}>
            <NearbyControl
              center={center}
              hasLocation={Boolean(center)}
              onCenterChange={(next) => {
                setCityDraft("");
                onCityChange(undefined);
                onLocationChange(next);
              }}
              onClearLocation={() => {
                setAreaDraft("");
                onClearLocation();
              }}
              locationCopy={
                center
                  ? `Showing tournaments within ${radiusLabel(radiusKm, measurementSystem)} of ${locationLabel || "your location"}.`
                  : "Choose an area to see nearby tournaments and distance."
              }
            />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Use a city search for this account. Precise and area-center
            coordinates are unavailable until location consent applies.
          </Typography>
        )}
        {!addressSearchAvailable ? (
          <TextField
            fullWidth
            size="small"
            label="City name"
            value={cityDraft}
            onChange={(event) => setCityDraft(event.target.value)}
            sx={{ mt: 1.5 }}
          />
        ) : null}
      </Box>

      <TextField
        select
        size="small"
        label="Distance"
        value={center ? radiusKm : ""}
        disabled={!center || !coordinateSearchAllowed}
        onChange={(event) => onRadiusChange(Number(event.target.value))}
        helperText={
          center
            ? "Results outside this distance are excluded."
            : "Choose an area or use your location first."
        }
      >
        <MenuItem value="" disabled>
          Choose location
        </MenuItem>
        {distanceOptions(radiusKm, measurementSystem).map((choice) => (
          <MenuItem key={choice.value} value={choice.value}>
            Within {choice.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        type="date"
        size="small"
        label="Starting on or after"
        value={startsAfter ?? ""}
        onChange={(event) =>
          onStartsAfterChange(event.target.value || undefined)
        }
        slotProps={{ inputLabel: { shrink: true } }}
        helperText="Uses each tournament venue's local calendar date."
      />

      <Box>
        <Typography
          component={expanded ? "h3" : "h2"}
          variant="subtitle1"
          sx={{ fontWeight: 800 }}
        >
          Events
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Show tournaments that contain at least one matching badminton event.
        </Typography>
        <Stack
          direction={expanded ? "column" : { xs: "column", sm: "row" }}
          spacing={1.5}
        >
          <TextField
            select
            fullWidth
            size="small"
            label="Tournament type"
            value={tournamentType ?? ""}
            onChange={(event) =>
              onTournamentTypeChange(
                (event.target.value || undefined) as
                  | "individual"
                  | "team"
                  | undefined,
              )
            }
          >
            <MenuItem value="">All types</MenuItem>
            <MenuItem value="individual">Individual and doubles</MenuItem>
            <MenuItem value="team">Team tournaments</MenuItem>
          </TextField>
          <TextField
            select
            fullWidth
            size="small"
            label="Event format"
            value={eventMode ?? ""}
            onChange={(event) =>
              onEventModeChange(
                (event.target.value || undefined) as
                  | "singles"
                  | "doubles"
                  | "mixed_doubles"
                  | undefined,
              )
            }
          >
            <MenuItem value="">All formats</MenuItem>
            <MenuItem value="singles">Singles</MenuItem>
            <MenuItem value="doubles">Doubles</MenuItem>
            <MenuItem value="mixed_doubles">Mixed doubles</MenuItem>
          </TextField>
          <TextField
            select
            fullWidth
            size="small"
            label="Grade"
            value={eventGrade ?? ""}
            onChange={(event) =>
              onEventGradeChange(
                (event.target.value || undefined) as
                  | "A"
                  | "B"
                  | "C"
                  | "D"
                  | "J"
                  | undefined,
              )
            }
          >
            <MenuItem value="">All grades</MenuItem>
            {(["A", "B", "C", "D", "J"] as const).map((grade) => (
              <MenuItem key={grade} value={grade}>
                {grade === "J" ? "Junior" : `Grade ${grade}`}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Box>

      <Box>
        <Typography
          component={expanded ? "h3" : "h2"}
          variant="subtitle1"
          sx={{ fontWeight: 800 }}
        >
          Venue
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Optional. Select one facility only when you already know where you
          want to play.
        </Typography>
        <FacilityScopePicker
          facilityId={facilityId}
          onChange={onFacilityChange}
        />
      </Box>
    </Stack>
  );

  if (expanded) {
    return (
      <Stack spacing={2.5}>
        {filterFields}
        <Stack direction="row" spacing={1}>
          <Button variant="contained" size="small" onClick={applyCity}>
            Apply filters
          </Button>
          <Button size="small" color="inherit" onClick={clearAll}>
            Clear all
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ alignItems: "center", flexWrap: "wrap" }}
      >
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          startIcon={<TuneRounded />}
          onClick={() => setOpen(true)}
          sx={{ borderRadius: 99, borderColor: "divider" }}
        >
          {activeCount ? `Filters (${activeCount})` : "Filters"}
        </Button>
        <Chip
          clickable
          icon={<VerifiedRoundedIcon aria-hidden />}
          label="Official only"
          color={officialOnly ? "primary" : "default"}
          variant={officialOnly ? "filled" : "outlined"}
          aria-pressed={officialOnly}
          onClick={() => onOfficialOnlyChange(!officialOnly)}
          sx={{ borderRadius: 99 }}
        />
        {center ? (
          <Chip
            variant="outlined"
            label={`${locationLabel || "Current location"} · ${radiusLabel(radiusKm, measurementSystem)}`}
            onDelete={onClearLocation}
            sx={{ borderRadius: 99, maxWidth: { xs: "100%", sm: 360 } }}
          />
        ) : city ? (
          <Chip
            variant="outlined"
            label={city}
            onDelete={() => {
              setCityDraft("");
              onCityChange(undefined);
            }}
            sx={{ borderRadius: 99 }}
          />
        ) : null}
        {dateLabel ? (
          <Chip
            variant="outlined"
            label={`From ${dateLabel}`}
            onDelete={() => onStartsAfterChange(undefined)}
            sx={{ borderRadius: 99 }}
          />
        ) : null}
        {activeCount || officialOnly ? (
          <Button size="small" color="inherit" onClick={clearAll}>
            Clear
          </Button>
        ) : null}
      </Stack>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        keepMounted
        fullWidth
        maxWidth="sm"
        aria-labelledby="tournament-filters-title"
        sx={{
          "& .MuiDialog-container": {
            alignItems: { xs: "flex-end", sm: "center" },
          },
          "& .MuiDialog-paper": {
            m: { xs: 0, sm: 3 },
            width: { xs: "100%", sm: "calc(100% - 48px)" },
            borderRadius: { xs: "20px 20px 0 0", sm: 2 },
            maxHeight: "88dvh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography component="span" id="tournament-filters-title">
            Find tournaments
          </Typography>
          <IconButton aria-label="Close filters" onClick={() => setOpen(false)}>
            <CloseRounded />
          </IconButton>
        </DialogTitle>
        <DialogContent>{filterFields}</DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" color="inherit" onClick={clearAll}>
            Clear all
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              applyCity();
              setOpen(false);
            }}
          >
            Show tournaments
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
